import React, { useCallback, useContext } from 'react';

import classNames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { Tooltip } from '@bringg/react-components';
import type { HeaderGroup } from 'react-table';
import { TypographyColor, TypographyHelper } from '@bringg-frontend/ui';

import '../react-table-config.d';
import { SELECTION_COLUMN_ID, ACTIONS_COLUMN_ID } from '../consts';
import TableOptionsContext from '../contexts/table-options-context';

interface TableHeaderProps<T extends Record<string, unknown>> {
	headerGroups: HeaderGroup<T>[];
	height?: number;
	style?: React.CSSProperties;
}

const SORT_TOOLTIP_ENTER_DELAY_SECONDS = 0.5;

const preventShiftClickTextSelection = (event: React.MouseEvent) => {
	if (event.shiftKey) {
		event.preventDefault();
	}
};

const TableHeader = <T extends Record<string, unknown>>(props: TableHeaderProps<T>) => {
	const { headerGroups, style, height } = props;
	const { showSortPriority: isSortPriorityEnabled, sortTooltip } = useContext(TableOptionsContext);

	const getSortIcon = useCallback(
		(
			{ id, isSorted, isSortedDesc, disableSortBy, canSort, sortedIndex }: HeaderGroup<T>,
			showSortPriority: boolean
		) => {
			return (
				canSort &&
				!disableSortBy && (
					<span className={classNames('sorter-wrapper', { sorted: isSorted })}>
						<BringgIcon
							iconName={isSortedDesc ? BringgFontIcons.ArrowDown : BringgFontIcons.ArrowUp}
							className={classNames({ active: isSorted })}
						/>
						{showSortPriority && isSorted && (
							<TypographyHelper
								tag="span"
								color={TypographyColor.Subtle}
								data-test-id={`sort-priority-${id}`}
							>
								{sortedIndex + 1}
							</TypographyHelper>
						)}
					</span>
				)
			);
		},
		[]
	);

	const getHeaderCell = useCallback(
		(column: HeaderGroup<T>, showSortPriority: boolean) => {
			if (column.id === SELECTION_COLUMN_ID) {
				return column.render('Header');
			}
			const sortProps = column.canSort
				? column.getSortByToggleProps(isSortPriorityEnabled ? { title: undefined } : undefined)
				: {};
			const headerCell = (
				<div
					{...sortProps}
					onMouseDown={isSortPriorityEnabled ? preventShiftClickTextSelection : undefined}
					className="header-wrapper"
				>
					<span className="text-ellipsis">{column.render('Header')}</span>
					{getSortIcon(column, showSortPriority)}
				</div>
			);

			return column.canSort && sortTooltip ? (
				<Tooltip title={sortTooltip} mouseEnterDelay={SORT_TOOLTIP_ENTER_DELAY_SECONDS}>
					{headerCell}
				</Tooltip>
			) : (
				headerCell
			);
		},
		[getSortIcon, isSortPriorityEnabled, sortTooltip]
	);

	return (
		<div className="table-header">
			<div className="table-header-inner" style={style}>
				{headerGroups.map(headerGroup => {
					const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps({
						style: { height }
					});
					const showSortPriority =
						isSortPriorityEnabled && headerGroup.headers.filter(column => column.isSorted).length > 1;

					return (
						<div key={headerGroupKey ?? headerGroup.id} {...headerGroupProps} className="table-row">
							{headerGroup.headers.map(column => {
								const headerProps = column.getHeaderProps();

								// TODO: strange behaviour need to be investigated:
								// for some reason sticky columns will have `position: relative` in their styles
								if (column.sticky) {
									if (headerProps.style) {
										headerProps.style.position = 'sticky';
									}
								}

								const { key: columnKey, ...restHeaderProps } = headerProps;

								return (
									<div
										key={columnKey ?? column.id}
										aria-label={column.id}
										{...restHeaderProps}
										className={classNames('table-header-cell', {
											'row-selection-cell': column.id === SELECTION_COLUMN_ID,
											'row-actions-cell': column.id === ACTIONS_COLUMN_ID
										})}
									>
										{getHeaderCell(column, showSortPriority)}
										{column.canResize && typeof column.getResizerProps === 'function' && (
											<div className="column-resizer">
												<span className="column-resizer-icon" {...column.getResizerProps()} />
											</div>
										)}
									</div>
								);
							})}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default TableHeader;
